import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import {
  Zap,
  Trophy,
  Target,
  BookOpen,
  Lock,
  CheckCircle2,
  Star,
  Heart,
  User,
  Users,
  Settings,
  LogOut,
  Snowflake,
  ArrowRight,
  Gift,
  Crown,
  RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authAPI, coursesAPI, progressAPI, gamificationAPI } from '../services/api';
import { LogoFull } from '../components/Logo';
import { useLanguage } from '../i18n/LanguageContext';
import StreakFreezeModal from '../components/StreakFreezeModal';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { getTranslatedField } from '../utils/languageUtils';
import { getLocalizedLesson } from '../utils/languageUtils';
import DailyQuestion from '../components/DailyQuestion';
import PremiumModal from '../components/PremiumModal';

const DashboardNew = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [user, setUser] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null); // Start with no course selected
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [streakFreezeModal, setStreakFreezeModal] = useState(false);
  const [streakFreezeAvailable, setStreakFreezeAvailable] = useState(true);
  const [premiumModal, setPremiumModal] = useState({ isOpen: false, variant: 'generic' });
  
  // Family Mode: Active profile state
  const [activeProfile, setActiveProfile] = useState(null);
  const [profileStats, setProfileStats] = useState(null);

  // Freemium access rules
  const FREE_LESSONS_PER_COURSE = 6;
  const TRANSVERSAL_FREE_LESSONS = {
    souffrance: 2,
    action_juste: 0,
    nondualite: 0,
    ego_illusion: 0,  // Premium direct
    compassion_ethique: 0,  // Premium direct
    eveil_authentique: 0  // Premium direct
  };

  const isPremium = user?.subscription_status === 'premium' || user?.subscription_status === 'active';

  // Check if a lesson is locked based on freemium rules
  const isLessonPremiumLocked = (lesson, courseSlug) => {
    if (isPremium) return false;
    
    if (courseSlug === 'transversal') {
      const parcours = lesson.parcours;
      const freeLimit = TRANSVERSAL_FREE_LESSONS[parcours] || 0;
      // Calculate position within parcours (1-5) for 6 parcours (30 lessons)
      let positionInParcours = ((lesson.order - 1) % 5) + 1;
      return positionInParcours > freeLimit;
    }
    
    return lesson.order > FREE_LESSONS_PER_COURSE;
  };

  // Load active profile from localStorage on mount
  useEffect(() => {
    const savedProfile = localStorage.getItem('spirify_active_profile');
    if (savedProfile) {
      try {
        setActiveProfile(JSON.parse(savedProfile));
      } catch (e) {
        console.warn('Invalid active profile in storage');
      }
    }
  }, []);

  useEffect(() => {
    loadData();
  }, []);
  
  // Reload profile stats when active profile changes
  useEffect(() => {
    if (user && activeProfile) {
      loadProfileStats();
    }
  }, [activeProfile, user]);

  useEffect(() => {
    if (selectedCourse) {
      loadLessons(selectedCourse);
      loadCompletedLessons(selectedCourse);
    }
  }, [selectedCourse, activeProfile]); // Reload when profile changes

  // Get highest completed lesson order to determine which lessons are unlocked
  const [completedLessons, setCompletedLessons] = useState({});
  
  const getHighestCompletedOrder = (courseId) => {
    return completedLessons[courseId]?.highest_completed_order || 0;
  };
  
  // Load profile-specific stats for Family Mode
  const loadProfileStats = async () => {
    if (!activeProfile?.id) return;
    
    try {
      const stats = await progressAPI.getProfileStats(activeProfile.id);
      setProfileStats(stats);
    } catch (error) {
      console.error('Error loading profile stats:', error);
      // If error (e.g., profile not found), clear active profile
      if (error.message?.includes('not found')) {
        localStorage.removeItem('spirify_active_profile');
        setActiveProfile(null);
        setProfileStats(null);
      }
    }
  };

  const loadData = async () => {
    try {
      const [userData, coursesData] = await Promise.all([
        authAPI.getCurrentUser(),
        coursesAPI.getAll()
      ]);
      
      setUser(userData);
      
      // Sort courses alphabetically, with "spiritualite" always last
      const sortedCourses = coursesData.sort((a, b) => {
        if (a.slug === 'spiritualite') return 1;
        if (b.slug === 'spiritualite') return -1;
        return a.name.localeCompare(b.name);
      });
      
      setCourses(sortedCourses);
      
      // Charger les leçons complétées pour tous les cours
      sortedCourses.forEach(course => {
        loadCompletedLessons(course.slug);
      });
      
      // Load streak freeze status
      await loadStreakFreezeStatus();
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const loadStreakFreezeStatus = async () => {
    try {
      const data = await gamificationAPI.getStreakFreezeStatus();
      setStreakFreezeAvailable(data.available);
    } catch (error) {
      console.error('Error loading streak freeze status:', error);
    }
  };

  const loadLessons = async (courseSlug) => {
    try {
      const lessonsData = await coursesAPI.getLessons(courseSlug);
      setLessons(lessonsData);
    } catch (error) {
      console.error('Error loading lessons:', error);
    }
  };

  const loadCompletedLessons = async (courseSlug) => {
    try {
      const data = await progressAPI.getCompletedLessons(courseSlug);
      setCompletedLessons(prev => ({
        ...prev,
        [courseSlug]: data
      }));
    } catch (error) {
      console.error('Error loading completed lessons:', error);
    }
  };

  // Fonction pour obtenir le nombre de leçons complétées pour un cours
  const getCompletedCount = (courseSlug) => {
    return completedLessons[courseSlug]?.completed_lesson_ids?.length || 0;
  };

  const handleLogout = () => {
    authAPI.logout();
  };

  const startLesson = (lessonId, lesson) => {
    // Check if lesson is premium locked
    if (lesson && isLessonPremiumLocked(lesson, selectedCourse)) {
      const variant = selectedCourse === 'transversal' ? 'transversal' : 'generic';
      setPremiumModal({ isOpen: true, variant });
      return;
    }
    navigate(`/lesson/${lessonId}?course=${selectedCourse}`);
  };

  // Trouver la prochaine leçon non complétée
  const getNextLesson = () => {
    if (!selectedCourse || !lessons.length) return null;
    
    const completed = completedLessons[selectedCourse]?.completed_lesson_ids || [];
    
    // Trouver la première leçon non complétée qui n'est pas premium locked
    const nextLesson = lessons.find(lesson => {
      const isCompleted = completed.includes(lesson.id);
      const isLocked = isLessonPremiumLocked(lesson, selectedCourse);
      return !isCompleted && !isLocked;
    });
    
    return nextLesson;
  };

  const continueToNextLesson = () => {
    const nextLesson = getNextLesson();
    if (nextLesson) {
      startLesson(nextLesson.id, nextLesson);
    }
  };

  const courseIcons = {
    islam: '☪️',
    christianisme: '✝️',
    judaisme: '✡️',
    spiritualite: '🌟',
    bouddhisme: '☸️',
    hindouisme: '🕉️',
    taoisme: '☯️',
    transversal: '🌐'
  };

  const courseColors = {
    islam: 'emerald',
    christianisme: 'blue',
    judaisme: 'indigo',
    spiritualite: 'purple',
    bouddhisme: 'amber',
    hindouisme: 'orange',
    taoisme: 'teal',
    transversal: 'cyan'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }
  
  // Get display stats (from profile if Family Mode is active, otherwise from user)
  const displayStats = profileStats && activeProfile 
    ? {
        xp: profileStats.xp_total,
        streak: profileStats.streak,
        hearts: profileStats.hearts,
        daily_xp: profileStats.is_main ? (profileStats.daily_xp || user?.daily_xp || 0) : (profileStats.daily_xp || 0),
        daily_goal: profileStats.daily_goal || user?.daily_goal || 50,
        displayName: profileStats.pseudo,
        avatar: profileStats.avatar
      }
    : {
        xp: user?.xp || 0,
        streak: user?.streak || 0,
        hearts: user?.hearts || 5,
        daily_xp: user?.daily_xp || 0,
        daily_goal: user?.daily_goal || 50,
        displayName: user?.username,
        avatar: "🧑"
      };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50/30 to-white">
      {/* Active Profile Banner - Only show when using a sub-profile */}
      {activeProfile && !activeProfile.is_main && (
        <div className="bg-gradient-to-r from-purple-600 to-pink-500 text-white py-2 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-lg">{activeProfile.avatar}</span>
              <span>{t?.family?.activeProfile || 'Profil actif'}: <strong>{activeProfile.pseudo}</strong></span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/family')}
              className="text-white hover:bg-white/20 text-xs"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              {t?.family?.switchProfile || 'Changer'}
            </Button>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div 
              onClick={() => navigate('/')}
              className="hover:opacity-80 transition-opacity cursor-pointer"
              style={{ cursor: 'pointer' }}
            >
              <LogoFull iconClassName="w-9 h-9" textClassName="text-2xl" />
            </div>

            <div className="flex items-center space-x-4">
              <LanguageSwitcher />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/leaderboard')}
                title={t?.dashboard?.leaderboard || "Classement"}
              >
                <Trophy className="w-5 h-5 text-amber-500" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/family')}
                title={t?.dashboard?.family || "Famille"}
              >
                <Users className="w-5 h-5 text-purple-500" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/settings')}
                title={t.dashboard.settings || "Paramètres"}
              >
                <Settings className="w-5 h-5 text-gray-600" />
              </Button>
              {/* Codes Promo - Only for admin */}
              {user?.email === 'helmi.mzah@gmail.com' && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/promo')}
                  title="Codes Promo"
                >
                  <Gift className="w-5 h-5 text-teal-600" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
              >
                <LogOut className="w-5 h-5 text-gray-600" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-3 space-y-6">
            {/* User Stats */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  {activeProfile && !activeProfile.is_main ? (
                    <>
                      <span className="text-xl">{displayStats.avatar}</span>
                      <span>{displayStats.displayName}</span>
                    </>
                  ) : (
                    <>
                      <User className="w-5 h-5" />
                      <span>{user?.username}</span>
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t.dashboard.xpEarned}</span>
                  <div className="flex items-center space-x-1">
                    <Star className="w-4 h-4 text-amber-500" />
                    <span className="font-bold text-lg">{displayStats.xp}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t.dashboard.streak}</span>
                  <div className="flex items-center space-x-1">
                    <Zap className="w-4 h-4 text-amber-500" />
                    <span className="font-bold">{displayStats.streak} {t.dashboard.streak}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    {t.dashboard.hearts} ❤️
                  </span>
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Heart
                        key={i}
                        className={`w-4 h-4 ${
                          i < displayStats.hearts
                            ? 'text-red-500 fill-red-500'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                
                {/* Hearts explanation */}
                {displayStats.hearts < 5 && (
                  <div className="text-xs text-gray-500 bg-red-50 p-2 rounded-md border border-red-100">
                    💡 {t.dashboard.heartsExplanation || "Vous perdez 1 cœur par erreur. Plus de cœurs = continuer à apprendre !"}
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">{t.dashboard.todaysGoal}</span>
                    <span className="text-xs text-gray-500">{displayStats.daily_xp}/{displayStats.daily_goal} XP</span>
                  </div>
                  <Progress value={(displayStats.daily_xp / displayStats.daily_goal) * 100 || 0} className="h-2" />
                </div>

                {/* Streak Freeze Indicator */}
                <div 
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                    streakFreezeAvailable 
                      ? 'bg-blue-50 border border-blue-200 hover:bg-blue-100' 
                      : 'bg-gray-50 border border-gray-200'
                  }`}
                  onClick={() => setStreakFreezeModal(true)}
                >
                  <div className="flex items-center gap-2">
                    <Snowflake className={`w-5 h-5 ${streakFreezeAvailable ? 'text-blue-500' : 'text-gray-400'}`} />
                    <span className="text-sm font-medium">Streak Freeze</span>
                  </div>
                  <Badge className={streakFreezeAvailable ? 'bg-blue-500' : 'bg-gray-400'}>
                    {streakFreezeAvailable ? '✓' : '✗'}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Gamification Quick Access */}
            <Card className="shadow-lg bg-gradient-to-br from-purple-50 to-pink-50">
              <CardHeader>
                <CardTitle className="text-sm font-semibold text-purple-700">
                  {t.dashboard.gamification || 'Progression'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2 hover:bg-white/80"
                  onClick={() => navigate('/badges')}
                >
                  <Trophy className="w-4 h-4 text-yellow-500" />
                  <span>{t.dashboard.badges || 'Badges'}</span>
                  <Badge className="ml-auto bg-yellow-500">{user?.badges?.length || 0}</Badge>
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2 hover:bg-white/80"
                  onClick={() => navigate('/goals')}
                >
                  <Target className="w-4 h-4 text-blue-500" />
                  <span>{t.dashboard.goals || 'Objectifs'}</span>
                </Button>
              </CardContent>
            </Card>

            {/* Course Selection */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle>{t.dashboard.chooseYourPath}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {courses.map((course) => {
                  // Get translated course name with proper key mapping
                  let courseKey = course.slug;
                  if (course.slug === 'christianisme') courseKey = 'christianism';
                  if (course.slug === 'judaisme') courseKey = 'judaism';
                  if (course.slug === 'spiritualite') courseKey = 'spirituality';
                  const courseName = t.dashboard[`${courseKey}Name`] || course.name;
                  
                  return (
                    <button
                      key={course.slug}
                      onClick={() => setSelectedCourse(course.slug)}
                      className={`w-full p-3 rounded-lg text-left transition-all ${
                        selectedCourse === course.slug
                          ? 'bg-purple-100 border-2 border-purple-500'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{course.icon || courseIcons[course.slug]}</span>
                        <div className="flex-1">
                          <div className="font-semibold">{courseName}</div>
                          <div className="text-xs text-gray-500">
                            {getCompletedCount(course.slug)}/{course.lesson_count} {t.dashboard.lessons}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Main Content - Lessons */}
          <div className="lg:col-span-9">
            {/* Welcome Message */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold mb-3">
                {t.dashboard.welcome}, {user?.username} ! 👋
              </h1>
              <p className="text-xl text-gray-600">
                {t.dashboard.chooseYourPath}
              </p>
            </div>

            {/* Daily Question */}
            <div className="mb-8">
              <DailyQuestion 
                user={user} 
                language={language} 
                translations={t}
                onXpEarned={(xp) => {
                  // Update user XP locally
                  setUser(prev => prev ? {...prev, xp: (prev.xp || 0) + xp} : prev);
                }}
              />
            </div>

            {/* Course Selection Screen - Show when no course selected */}
            {!selectedCourse ? (
              <div className="text-center py-12">
                <h2 className="text-3xl font-bold mb-8">
                  {t.dashboard.questionPrompt}
                </h2>
                <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                  {courses.map((course) => {
                    // Get translated course name and description with proper key mapping
                    let courseKey = course.slug;
                    if (course.slug === 'christianisme') courseKey = 'christianism';
                    if (course.slug === 'judaisme') courseKey = 'judaism';
                    if (course.slug === 'spiritualite') courseKey = 'spirituality';
                    const courseName = t.dashboard[`${courseKey}Name`] || course.name;
                    const courseDesc = t.dashboard[`${courseKey}Desc`] || course.description;
                    
                    return (
                      <Card
                        key={course.slug}
                        className="cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 hover:border-purple-500"
                        onClick={() => setSelectedCourse(course.slug)}
                      >
                        <CardContent className="p-8">
                          <div className="text-6xl mb-4">{course.icon}</div>
                          <h3 className="text-2xl font-bold mb-2">{courseName}</h3>
                          <p className="text-gray-600 mb-4">{courseDesc}</p>
                          <div className="text-sm text-purple-600 font-semibold">
                            {getCompletedCount(course.slug)}/{course.lesson_count} {t.dashboard.lessons} • {t.dashboard.clickToStart}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>
                {/* Course Header - Show when course selected */}
                <div className="mb-6">
                  {(() => {
                    const course = courses.find(c => c.slug === selectedCourse);
                    if (!course) return null;
                    
                    // Get translated course name and description with proper key mapping
                    let courseKey = selectedCourse;
                    if (selectedCourse === 'christianisme') courseKey = 'christianism';
                    if (selectedCourse === 'judaisme') courseKey = 'judaism';
                    if (selectedCourse === 'spiritualite') courseKey = 'spirituality';
                    const courseName = t.dashboard[`${courseKey}Name`] || course.name;
                    const courseDesc = t.dashboard[`${courseKey}Desc`] || course.description;
                    
                    return (
                      <>
                        <h2 className="text-2xl font-bold mb-2 flex items-center space-x-2">
                          <span>{course.icon}</span>
                          <span>{courseName}</span>
                        </h2>
                        <p className="text-gray-600">{courseDesc}</p>
                      </>
                    );
                  })()}
                </div>
              </>
            )}

            {/* Only show lessons when a course is selected */}
            {selectedCourse && (
              <>
                {/* Continue Button - Show next lesson to do */}
                {getNextLesson() && (
                  <Card className="mb-6 bg-gradient-to-r from-teal-600 to-emerald-600 text-white border-0 shadow-xl">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-bold mb-2">
                            {completedLessons[selectedCourse]?.completed_lesson_ids?.length > 0 
                              ? t.dashboard.resume || "Reprendre" 
                              : t.dashboard.start || "Commencer"}
                          </h3>
                          <p className="text-sm opacity-90">
                            {t.lesson.lessonProgress || "Leçon"} {getNextLesson().order}: {getTranslatedField(getNextLesson(), 'title', language)}
                          </p>
                        </div>
                        <Button
                          onClick={continueToNextLesson}
                          className="bg-white text-teal-700 hover:bg-gray-100 font-bold px-8 py-6 text-lg"
                        >
                          {completedLessons[selectedCourse]?.completed_lesson_ids?.length > 0 
                            ? t.dashboard.continue || "Continuer" 
                            : t.dashboard.start || "Commencer"}
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Premium Banner */}
                {(user?.subscription_status === 'free' || !user?.subscription_status) && (
                  <Card className="mb-6 bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-0 shadow-xl">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-bold mb-2">🌟 {t.pricing.premium}!</h3>
                          <p className="text-sm opacity-90">{t.pricing.features} 160 {t.dashboard.lessons} - 4.99€{t.pricing.perMonth}</p>
                        </div>
                        <Button
                          onClick={() => navigate('/pricing')}
                          className="bg-white text-purple-700 hover:bg-gray-100 font-bold px-6"
                        >
                          {t.nav.pricing}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Lessons Grid */}
                {selectedCourse === 'transversal' ? (
                  // Special display for Transversal course - by parcours
                  <TransversalLessons 
                    lessons={lessons}
                    language={language}
                    t={t}
                    isPremium={isPremium}
                    isLessonPremiumLocked={isLessonPremiumLocked}
                    completedLessons={completedLessons}
                    selectedCourse={selectedCourse}
                    getHighestCompletedOrder={getHighestCompletedOrder}
                    startLesson={startLesson}
                    setPremiumModal={setPremiumModal}
                  />
                ) : (
                  // Regular course display
                  <div className="grid md:grid-cols-2 gap-4">
                    {lessons.map((lesson) => {
                      const premiumLocked = isLessonPremiumLocked(lesson, selectedCourse);
                      const highestCompleted = getHighestCompletedOrder(selectedCourse);
                      const isProgressionLocked = !premiumLocked && lesson.order > (highestCompleted + 1);
                      const isCompleted = completedLessons[selectedCourse]?.completed_lesson_ids?.includes(lesson.id);
                      const isLocked = premiumLocked || isProgressionLocked;
                      
                      return (
                        <Card
                          key={lesson.id}
                          data-testid={`lesson-card-${lesson.order}`}
                          className={`shadow-lg transition-all ${
                            isLocked
                              ? 'opacity-75 cursor-pointer hover:opacity-90'
                              : 'hover:shadow-xl hover:scale-[1.02] cursor-pointer'
                          } ${isCompleted ? 'border-green-200 bg-green-50/30' : ''}`}
                          onClick={() => startLesson(lesson.id, lesson)}
                        >
                          <CardContent className="p-6">
                            <div className="flex items-start space-x-4">
                              <div className="text-4xl">{lesson.icon}</div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <Badge variant="outline" className="text-xs">
                                    {t.lesson?.lessonProgress || "Leçon"} {lesson.order}
                                  </Badge>
                                  {premiumLocked && (
                                    <Badge className="bg-purple-600 text-white text-xs">
                                      <Lock className="w-3 h-3 mr-1" />
                                      Premium
                                    </Badge>
                                  )}
                                  {isProgressionLocked && !premiumLocked && (
                                    <Lock className="w-4 h-4 text-gray-400" />
                                  )}
                                  {isCompleted && (
                                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                                  )}
                                </div>
                                <h3 className="font-bold text-lg mb-1">{getTranslatedField(lesson, 'title', language)}</h3>
                                <p className="text-sm text-gray-600 mb-3">{getTranslatedField(lesson, 'description', language)}</p>
                                <div className="flex items-center space-x-4 text-sm text-gray-500">
                                  <span className="flex items-center space-x-1">
                                    <Star className="w-4 h-4 text-amber-500" />
                                    <span>{lesson.xp} {t.dashboard?.xp || 'XP'}</span>
                                  </span>
                                  <span>{lesson.exercise_count} {t.dashboard?.questions || 'Questions'}</span>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Streak Freeze Modal */}
      <StreakFreezeModal
        isOpen={streakFreezeModal}
        onClose={() => setStreakFreezeModal(false)}
        onUse={async () => {
          // Handle streak freeze use if needed
          await loadStreakFreezeStatus();
          setStreakFreezeModal(false);
        }}
        streakFreezeAvailable={streakFreezeAvailable}
        streak={user?.streak || 0}
      />

      {/* Premium Modal */}
      <PremiumModal
        isOpen={premiumModal.isOpen}
        onClose={() => setPremiumModal({ isOpen: false, variant: 'generic' })}
        variant={premiumModal.variant}
      />
    </div>
  );
};

// Transversal Lessons Component - displays lessons by parcours
const TransversalLessons = ({ 
  lessons, 
  language, 
  t, 
  isPremium, 
  isLessonPremiumLocked, 
  completedLessons, 
  selectedCourse,
  getHighestCompletedOrder,
  startLesson,
  setPremiumModal
}) => {
  const parcours = [
    { 
      id: 'souffrance', 
      icon: '🔥', 
      name: { fr: 'Souffrance & libération', en: 'Suffering & Liberation', es: 'Sufrimiento y liberación', it: 'Sofferenza e liberazione', pt: 'Sofrimento e libertação', ar: 'المعاناة والتحرر' },
      desc: { fr: 'Comprendre l\'origine de la souffrance et les chemins de libération', en: 'Understanding the origin of suffering and paths to liberation', es: 'Comprender el origen del sufrimiento y los caminos de liberación', it: 'Comprendere l\'origine della sofferenza e i percorsi di liberazione', pt: 'Compreender a origem do sofrimento e os caminhos de libertação', ar: 'فهم أصل المعاناة وطرق التحرر' }
    },
    { 
      id: 'action_juste', 
      icon: '⚖️', 
      name: { fr: 'Action juste', en: 'Right Action', es: 'Acción justa', it: 'Azione giusta', pt: 'Ação justa', ar: 'الفعل الصحيح' },
      desc: { fr: 'Agir avec discernement et intention', en: 'Acting with discernment and intention', es: 'Actuar con discernimiento e intención', it: 'Agire con discernimento e intenzione', pt: 'Agir com discernimento e intenção', ar: 'التصرف بتمييز ونية' }
    },
    { 
      id: 'nondualite', 
      icon: '🧠', 
      name: { fr: 'Non-dualité & illusion', en: 'Non-duality & Illusion', es: 'No-dualidad e ilusión', it: 'Non-dualità e illusione', pt: 'Não-dualidade e ilusão', ar: 'اللاثنائية والوهم' },
      desc: { fr: 'Explorer la nature de la réalité et du moi', en: 'Exploring the nature of reality and self', es: 'Explorar la naturaleza de la realidad y del yo', it: 'Esplorare la natura della realtà e del sé', pt: 'Explorar a natureza da realidade e do eu', ar: 'استكشاف طبيعة الواقع والذات' }
    },
    { 
      id: 'ego_illusion', 
      icon: '👁️', 
      name: { fr: 'Ego & illusion spirituelle', en: 'Ego & Spiritual Illusion', es: 'Ego e ilusión espiritual', it: 'Ego e illusione spirituale', pt: 'Ego e ilusão espiritual', ar: 'الأنا والوهم الروحي' },
      desc: { fr: 'Déjouer les pièges de l\'ego sur le chemin spirituel', en: 'Navigating ego traps on the spiritual path', es: 'Sortear las trampas del ego en el camino espiritual', it: 'Navigare le trappole dell\'ego sul cammino spirituale', pt: 'Navegar as armadilhas do ego no caminho espiritual', ar: 'تجنب فخاخ الأنا في المسار الروحي' }
    },
    { 
      id: 'compassion_ethique', 
      icon: '🤍', 
      name: { fr: 'Compassion & éthique', en: 'Compassion & Ethics', es: 'Compasión y ética', it: 'Compassione ed etica', pt: 'Compaixão e ética', ar: 'الرحمة والأخلاق' },
      desc: { fr: 'Le cœur humain et moral des traditions', en: 'The human and moral heart of traditions', es: 'El corazón humano y moral de las tradiciones', it: 'Il cuore umano e morale delle tradizioni', pt: 'O coração humano e moral das tradições', ar: 'القلب الإنساني والأخلاقي للتقاليد' }
    },
    { 
      id: 'eveil_authentique', 
      icon: '🌅', 
      name: { fr: 'Éveil authentique vs illusion', en: 'Authentic Awakening vs Illusion', es: 'Despertar auténtico vs ilusión', it: 'Risveglio autentico vs illusione', pt: 'Despertar autêntico vs ilusão', ar: 'الصحوة الحقيقية مقابل الوهم' },
      desc: { fr: 'Distinguer transformation réelle et illusion spirituelle', en: 'Distinguishing real transformation from spiritual illusion', es: 'Distinguir la transformación real de la ilusión espiritual', it: 'Distinguere la trasformazione reale dall\'illusione spirituale', pt: 'Distinguir transformação real de ilusão espiritual', ar: 'التمييز بين التحول الحقيقي والوهم الروحي' }
    }
  ];

  return (
    <div className="space-y-8">
      {parcours.map((p) => {
        const parcoursLessons = lessons.filter(l => l.parcours === p.id);
        
        return (
          <div key={p.id} className="space-y-4" data-testid={`parcours-${p.id}`}>
            {/* Parcours Header */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">{p.icon}</span>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{p.name[language] || p.name.fr}</h3>
                <p className="text-sm text-gray-500">{p.desc[language] || p.desc.fr}</p>
              </div>
            </div>

            {/* Lessons Grid */}
            <div className="grid md:grid-cols-2 gap-4">
              {parcoursLessons.map((lesson) => {
                const premiumLocked = isLessonPremiumLocked(lesson, selectedCourse);
                const highestCompleted = getHighestCompletedOrder(selectedCourse);
                const isProgressionLocked = !premiumLocked && lesson.order > (highestCompleted + 1);
                const isCompleted = completedLessons[selectedCourse]?.completed_lesson_ids?.includes(lesson.id);
                const isLocked = premiumLocked || isProgressionLocked;
                // Calculate position within current parcours (1-5)
                const positionInParcours = ((lesson.order - 1) % 5) + 1;

                return (
                  <Card
                    key={lesson.id}
                    data-testid={`lesson-card-${lesson.order}`}
                    className={`shadow-lg transition-all ${
                      premiumLocked
                        ? 'opacity-75 cursor-pointer hover:opacity-90 border-purple-200'
                        : isProgressionLocked
                          ? 'opacity-60 cursor-not-allowed'
                          : 'hover:shadow-xl hover:scale-[1.02] cursor-pointer'
                    } ${isCompleted ? 'border-green-200 bg-green-50/30' : ''}`}
                    onClick={() => {
                      if (premiumLocked) {
                        setPremiumModal({ isOpen: true, variant: 'transversal' });
                      } else if (!isProgressionLocked) {
                        startLesson(lesson.id, lesson);
                      }
                    }}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="text-3xl">{lesson.icon}</div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge variant="outline" className="text-xs">
                              {positionInParcours}/5
                            </Badge>
                            {premiumLocked && (
                              <Badge className="bg-purple-600 text-white text-xs">
                                <Lock className="w-3 h-3 mr-1" />
                                Premium
                              </Badge>
                            )}
                            {isProgressionLocked && !premiumLocked && (
                              <Lock className="w-4 h-4 text-gray-400" />
                            )}
                            {isCompleted && (
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                            )}
                          </div>
                          <h4 className="font-semibold text-base mb-1">{getTranslatedField(lesson, 'title', language)}</h4>
                          <div className="flex items-center gap-3 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Star className="w-3 h-3 text-amber-500" />
                              <span>{lesson.xp} XP</span>
                            </span>
                            <span>{lesson.exercise_count} Q</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DashboardNew;